import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  Avatar,
  Fade,
  Divider
} from '@mui/material';
import {
  School,
  EmojiEvents,
  Favorite,
  Lightbulb,
  Groups,
  Timeline,
  Star,
  CheckCircle
} from '@mui/icons-material';

const About = () => {
  const values = [
    {
      title: 'Excellence',
      description: 'We strive for the highest standards in all that we do.',
      icon: EmojiEvents,
      color: '#050979'
    },
    {
      title: 'Integrity',
      description: 'We act with honesty, respect, and moral courage.',
      icon: Favorite,
      color: '#d32f2f'
    },
    {
      title: 'Innovation',
      description: 'We embrace creativity and forward-thinking approaches.',
      icon: Lightbulb,
      color: '#ed6c02'
    },
    {
      title: 'Community',
      description: 'We foster a supportive and inclusive environment.',
      icon: Groups,
      color: '#2e7d32'
    }
  ];

  const achievements = [
    'Founded in 2010 with a vision for excellence',
    'Recognized institution of academic achievement',
    'Dedicated faculty and staff committed to student success',
    'Growing community of learners and leaders'
  ];

  return (
    <Box>
      {/* Modern Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #050979 0%, #42a5f5 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                About Eagles Refuge Academy
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.9,
                  fontWeight: 300,
                  maxWidth: 800,
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                Discover our mission, values, and commitment to educational excellence
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Mission Section */}
        <Fade in timeout={1200}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 4, md: 6 },
              mb: 8,
              borderRadius: 4,
              background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 56,
                  height: 56,
                  mr: 3
                }}
              >
                <School sx={{ fontSize: 28 }} />
              </Avatar>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main'
                }}
              >
                Our Mission
              </Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontSize: '1.25rem',
                lineHeight: 1.8,
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              Eagles Refuge Academy is dedicated to providing a nurturing, challenging, and inspiring
              educational environment where students develop academically, socially, and morally to
              become confident, compassionate, and capable leaders of tomorrow.
            </Typography>
          </Paper>
        </Fade>

        {/* Values Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.main'
              }}
            >
              Our Values
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              The core principles that guide everything we do
            </Typography>
          </Box>

          <Grid
            container
            spacing={4}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'stretch'
            }}
          >
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={value.title}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <Fade in timeout={1000 + index * 200}>
                    <Card
                      elevation={4}
                      sx={{
                        width: '100%',
                        maxWidth: 280,
                        height: 320,
                        borderRadius: 3,
                        transition: 'all 0.3s ease-in-out',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: 6
                        }
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%'
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: value.color,
                            width: 64,
                            height: 64,
                            mb: 3
                          }}
                        >
                          <IconComponent sx={{ fontSize: 32 }} />
                        </Avatar>

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            color: 'primary.main'
                          }}
                        >
                          {value.title}
                        </Typography>

                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                            textAlign: 'center'
                          }}
                        >
                          {value.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* History Section */}
        <Fade in timeout={1400}>
          <Box>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'secondary.main',
                      width: 56,
                      height: 56,
                      mr: 3
                    }}
                  >
                    <Timeline sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main'
                    }}
                  >
                    Our History
                  </Typography>
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1.2rem',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    mb: 4
                  }}
                >
                  Founded in 2010, Eagles Refuge Academy has grown from a small community school
                  to a recognized institution of academic excellence. Our dedicated faculty and
                  staff work tirelessly to ensure every student reaches their full potential.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="Since 2010"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label="Academic Excellence"
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  elevation={3}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #f3f4f6 0%, #ffffff 100%)',
                    width: '100%',
                    maxWidth: 500
                  }}
                >
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main'
                      }}
                    >
                      Our Journey
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {achievements.map((achievement, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 2,
                          width: '100%',
                          maxWidth: 400
                        }}
                      >
                        <CheckCircle
                          sx={{
                            color: 'success.main',
                            mr: 2,
                            mt: 0.5,
                            fontSize: 20,
                            flexShrink: 0
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            textAlign: 'left'
                          }}
                        >
                          {achievement}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Call to Action Section */}
        <Fade in timeout={1600}>
          <Paper
            elevation={4}
            sx={{
              mt: 8,
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(135deg, #050979 0%, #42a5f5 100%)',
              color: 'white'
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2
              }}
            >
              Ready to Join Our Community?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                mb: 3,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Discover how Eagles Refuge Academy can help your child soar to new heights
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label="Excellence"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
                icon={<Star sx={{ color: 'white !important' }} />}
              />
              <Chip
                label="Innovation"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
                icon={<Lightbulb sx={{ color: 'white !important' }} />}
              />
              <Chip
                label="Community"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
                icon={<Groups sx={{ color: 'white !important' }} />}
              />
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default About;
